// src/screens/GoogleCallback.js
// ─────────────────────────────────────────────────────────────────────────────
// Handles the redirect from Google OAuth.
// The backend sends the user to /auth/google/callback (or /dashboard) with
// tokens in the URL query string. This component:
//   1. Reads access_token / refresh_token / user_id / role / email from params
//   2. Saves them to localStorage
//   3. Updates AuthContext via login()
//   4. Navigates to /dm-activity
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Loader2 } from 'lucide-react';

const GoogleCallback = () => {
  const navigate       = useNavigate();
  const { login }      = useAuth();
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    // ── Error from backend ─────────────────────────────────────────────
    const oauthError = params.get('error');
    if (oauthError) {
      setError(decodeURIComponent(oauthError));
      setTimeout(() => navigate('/auth?error=' + encodeURIComponent(oauthError)), 2000);
      return;
    }

    // ── Token params — backend may use any of these key names ──────────
    const accessToken  = params.get('access_token')  || params.get('token');
    const refreshToken = params.get('refresh_token');
    const userId       = params.get('user_id')       || params.get('id');
    const role         = params.get('role')           || '';
    const userEmail    = params.get('email')          || params.get('user_email') || '';

    if (accessToken) {
      // Save to localStorage
      localStorage.setItem('access_token',  accessToken);
      if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
      localStorage.setItem('user_id',    userId    || '');
      localStorage.setItem('role',       role);
      localStorage.setItem('user_email', userEmail);

      // Update AuthContext
      login(
        { access_token: accessToken, refresh_token: refreshToken, user_id: userId, role },
        userEmail
      );

      // Clean URL then go to dm-activity
      window.history.replaceState({}, document.title, window.location.pathname);
      navigate('/getstarted', { replace: true });
      return;
    }

    // ── No token in URL — backend may have used a cookie/session ──────
    // Check if we already have a token saved (e.g. backend set it via cookie
    // and a previous redirect already saved it), then just go to dm-activity.
    const existingToken = localStorage.getItem('access_token');
    if (existingToken) {
      navigate('/getstarted', { replace: true });
      return;
    }

    // ── Nothing found — send back to login with error ──────────────────
    setError('Authentication failed. Please try again.');
    setTimeout(() => navigate('/auth'), 2500);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0a0b14',
      gap: '1rem',
    }}>
      {error ? (
        <>
          <p style={{ color: '#EF4444', fontSize: '0.9rem', fontWeight: 600 }}>{error}</p>
          <p style={{ color: '#6B7280', fontSize: '0.75rem' }}>Redirecting to login…</p>
        </>
      ) : (
        <>
          <Loader2
            size={32}
            style={{ color: '#6366F1', animation: 'spin 0.8s linear infinite' }}
          />
          <p style={{ color: '#9CA3AF', fontSize: '0.85rem', fontWeight: 500 }}>
            Completing sign-in…
          </p>
        </>
      )}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
};

export default GoogleCallback;